import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Calendar, Clock, MapPin, Users, Luggage, AlertCircle, X, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BookingEdit = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [canEdit, setCanEdit] = useState(false);
  const [hoursUntil, setHoursUntil] = useState(0);
  const [booking, setBooking] = useState(null);
  const [formData, setFormData] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const fetchBookingData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Fetch booking details
      const bookingRes = await axios.get(`${API}/bookings/${bookingId}`);
      const bookingData = bookingRes.data;
      setBooking(bookingData);
      setFormData(bookingData);
      
      // Check if can edit
      const eligibilityRes = await axios.get(`${API}/bookings/${bookingId}/can-edit`);
      const eligibility = eligibilityRes.data;
      setCanEdit(eligibility.canEdit);
      setHoursUntil(eligibility.hoursUntilPickup);
      
      setLoading(false);
    } catch (error) {
      console.error('Error fetching booking:', error);
      toast.error('Failed to load booking');
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    fetchBookingData();
  }, [fetchBookingData]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!canEdit) {
      toast.error('Cannot modify booking within 72 hours of pickup');
      return;
    }

    setSubmitting(true);
    try {
      await axios.put(`${API}/bookings/${bookingId}`, formData);
      toast.success('Booking updated successfully!');
      setTimeout(() => navigate('/'), 2000);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to update booking';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/bookings/${bookingId}/cancel`);
      toast.success('Booking cancelled successfully');
      setShowCancelModal(false);
      setTimeout(() => navigate('/'), 2000);
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to cancel booking';
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
          <p className="text-gray-600">Loading booking...</p>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="p-8 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Booking Not Found</h2>
          <p className="text-gray-600 mb-4">The booking you're looking for doesn't exist.</p>
          <Button onClick={() => navigate('/')}>Go Home</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-36 pb-12">
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-black mb-2">Edit Booking</h1>
          <p className="text-gray-600">Booking ID: {bookingId}</p>
        </div>

        {/* Countdown Alert */}
        <Card className={`p-6 mb-6 ${canEdit ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'}`}>
          <div className="flex items-start gap-4">
            {canEdit ? (
              <Check className="h-6 w-6 text-green-600 flex-shrink-0" />
            ) : (
              <AlertCircle className="h-6 w-6 text-red-600 flex-shrink-0" />
            )}
            <div className="flex-1">
              <h3 className={`font-bold text-lg mb-1 ${canEdit ? 'text-green-900' : 'text-red-900'}`}>
                {canEdit ? 'Booking Can Be Modified' : 'Modifications Not Allowed'}
              </h3>
              <p className={canEdit ? 'text-green-700' : 'text-red-700'}>
                {canEdit ? (
                  <>You can modify or cancel this booking up to 48 hours before pickup.</>
                ) : (
                  <>Modifications are only allowed up to 48 hours before pickup.</>
                )}
              </p>
            </div>
          </div>
        </Card>

        {/* Return Journey (read-only) */}
        {booking.returnRequired === 'yes' && (
          <Card className="p-6 mb-6 border-2 border-indigo-200 bg-indigo-50/50" data-testid="booking-edit-return-journey">
            <h3 className="text-sm font-bold uppercase tracking-wider text-indigo-700 mb-3">Return Journey</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500 mb-1">Return Date &amp; Time</p>
                <p className="font-semibold text-black">{booking.returnDate} at {booking.returnTime}</p>
              </div>
              <div>
                <p className="text-gray-500 mb-1">Return Route</p>
                <p className="font-semibold text-black">{booking.dropoffLocation} &rarr; {booking.pickupLocation}</p>
              </div>
            </div>
          </Card>
        )}

        {/* Edit Form */}
        <form onSubmit={handleSubmit}>
          <Card className="p-8 mb-6">
            <h2 className="text-2xl font-bold text-black mb-6">Booking Details</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Pickup Location */}
              <div>
                <Label className="text-gray-900 mb-2 font-medium flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Pickup Location
                </Label>
                <Input
                  value={formData.pickupLocation || ''}
                  onChange={(e) => handleChange('pickupLocation', e.target.value)}
                  disabled={!canEdit}
                  className="bg-white border-gray-300 text-gray-900"
                />
              </div>

              {/* Drop-off Location */}
              <div>
                <Label className="text-gray-900 mb-2 font-medium flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Drop-off Location
                </Label>
                <Input
                  value={formData.dropoffLocation || ''}
                  onChange={(e) => handleChange('dropoffLocation', e.target.value)}
                  disabled={!canEdit}
                  className="bg-white border-gray-300 text-gray-900"
                />
              </div>

              {/* Pickup Date */}
              <div>
                <Label className="text-gray-900 mb-2 font-medium flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Pickup Date
                </Label>
                <Input
                  type="date"
                  value={formData.pickupDate || ''}
                  onChange={(e) => handleChange('pickupDate', e.target.value)}
                  disabled={!canEdit}
                  className="bg-white border-gray-300 text-gray-900"
                />
              </div>

              {/* Pickup Time */}
              <div>
                <Label className="text-gray-900 mb-2 font-medium flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Pickup Time
                </Label>
                <Input
                  type="time"
                  value={formData.pickupTime || ''}
                  onChange={(e) => handleChange('pickupTime', e.target.value)}
                  disabled={!canEdit}
                  className="bg-white border-gray-300 text-gray-900"
                />
              </div>

              {/* Flight Number */}
              <div>
                <Label className="text-gray-900 mb-2 font-medium">
                  Flight Number (Optional)
                </Label>
                <Input
                  value={formData.flightNumber || ''}
                  onChange={(e) => handleChange('flightNumber', e.target.value)}
                  disabled={!canEdit}
                  placeholder="e.g. BA123"
                  className="bg-white border-gray-300 text-gray-900"
                />
              </div>

              {/* Passengers */}
              <div>
                <Label className="text-gray-900 mb-2 font-medium flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Passengers
                </Label>
                <Input
                  type="number"
                  value={formData.passengers || ''}
                  onChange={(e) => handleChange('passengers', e.target.value)}
                  disabled={!canEdit}
                  min="1"
                  className="bg-white border-gray-300 text-gray-900"
                />
              </div>

              {/* Luggage */}
              <div>
                <Label className="text-gray-900 mb-2 font-medium flex items-center gap-2">
                  <Luggage className="h-4 w-4" />
                  Luggage Pieces
                </Label>
                <Input
                  type="number"
                  value={formData.luggage || ''}
                  onChange={(e) => handleChange('luggage', e.target.value)}
                  disabled={!canEdit}
                  min="0"
                  className="bg-white border-gray-300 text-gray-900"
                />
              </div>

              {/* Child Seats */}
              <div>
                <Label className="text-gray-900 mb-2 font-medium">
                  Child Seats
                </Label>
                <Input
                  type="number"
                  value={formData.childSeats || '0'}
                  onChange={(e) => handleChange('childSeats', e.target.value)}
                  disabled={!canEdit}
                  min="0"
                  className="bg-white border-gray-300 text-gray-900"
                />
              </div>

              {/* Special Requests */}
              <div className="md:col-span-2">
                <Label className="text-gray-900 mb-2 font-medium">
                  Special Requests
                </Label>
                <Textarea
                  value={formData.specialRequests || ''}
                  onChange={(e) => handleChange('specialRequests', e.target.value)}
                  disabled={!canEdit}
                  rows={4}
                  className="bg-white border-gray-300 text-gray-900"
                />
              </div>
            </div>
          </Card>

          {/* Action Buttons */}
          <div className="flex justify-between gap-4">
            <Button
              type="button"
              onClick={() => navigate('/')}
              variant="outline"
              className="border-gray-300"
            >
              Back to Home
            </Button>
            
            <div className="flex gap-4">
              {canEdit && (
                <>
                  <Button
                    type="button"
                    onClick={() => setShowCancelModal(true)}
                    variant="outline"
                    className="border-red-500 text-red-600 hover:bg-red-50"
                  >
                    Cancel Booking
                  </Button>
                  <Button
                    type="submit"
                    disabled={submitting}
                    className="bg-black text-white hover:bg-gray-800"
                  >
                    {submitting ? 'Saving...' : 'Save Changes'}
                  </Button>
                </>
              )}
            </div>
          </div>
        </form>

        {/* Cancel Confirmation Modal */}
        {showCancelModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <Card className="max-w-md w-full p-6">
              <div className="flex justify-between items-start mb-4">
                <h3 className="text-xl font-bold text-black">Cancel Booking?</h3>
                <button onClick={() => setShowCancelModal(false)}>
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              <p className="text-gray-600 mb-6">
                Are you sure you want to cancel this booking? This action cannot be undone.
              </p>
              
              <div className="flex gap-3">
                <Button
                  onClick={() => setShowCancelModal(false)}
                  variant="outline"
                  className="flex-1"
                >
                  Keep Booking
                </Button>
                <Button
                  onClick={handleCancel}
                  disabled={submitting}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {submitting ? 'Cancelling...' : 'Yes, Cancel'}
                </Button>
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookingEdit;
